<script setup lang="ts">
import type { ItemKind } from '@satisa-memory/shared'
import { computed } from 'vue'
import { type EntityIcon, sourceMeta } from '../lib/format'

const props = withDefaults(
  defineProps<{
    source: string
    kind?: string | null
    itemKind?: ItemKind | null
    size?: number
  }>(),
  { size: 20, kind: null, itemKind: null }
)
const meta = computed(() => sourceMeta(props.source, props.kind, props.itemKind))

/** Lucide outlines (ISC), on a 24-unit grid: file-text, notebook-pen, square-check, scale. */
const ICONS: Record<EntityIcon, string[]> = {
  brief: [
    'M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z',
    'M14 2v4a2 2 0 0 0 2 2h4',
    'M16 13H8',
    'M16 17H8',
    'M10 9H8'
  ],
  debrief: [
    'M13.4 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7.4',
    'M2 6h4',
    'M2 10h4',
    'M2 14h4',
    'M2 18h4',
    'M21.378 5.626a1 1 0 1 0-3.004-3.004l-5.01 5.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z'
  ],
  todo: [
    'M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z',
    'm9 12 2 2 4-4'
  ],
  decision: [
    'm16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z',
    'm2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z',
    'M7 21h10',
    'M12 3v18',
    'M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2'
  ]
}
const icon = computed(() => (meta.value.icon ? ICONS[meta.value.icon] : null))
</script>

<template>
  <span
    class="badge"
    :style="{
      background: meta.color,
      width: `${size}px`,
      height: `${size}px`,
      fontSize: `${Math.round(size * 0.55)}px`
    }"
    :title="meta.label"
    role="img"
    :aria-label="meta.label"
    ><svg
      v-if="icon"
      viewBox="0 0 24 24"
      :width="Math.round(size * 0.68)"
      :height="Math.round(size * 0.68)"
      fill="none"
      stroke="currentColor"
      :stroke-width="size < 16 ? 2.6 : 2.2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <path v-for="d in icon" :key="d" :d="d" /></svg
    ><template v-else>{{ meta.mark }}</template></span
  >
</template>

<style scoped>
.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 5px;
  color: #fff;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.12);
  user-select: none;
}
</style>
